import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { bmxurl, getAccess } from 'src/globals';
import { BusyService } from '../_services/busy.service';

export interface PortalStatItem {
  venueid: string;
  value: number;
  action: string;
}

export type PortalRuleAccess = 'hidden' | 'view' | 'edit';
export interface PortalRule {
  key: string;
  access: PortalRuleAccess;
}

export interface PortalYesNoRule {
  key: string;
  enabled: boolean;
}

export interface CustomerPortalTemplate {
  id: string;
  portaltemplateid: string;
  venueid: string;
  name: string;
  colour: string;
  disabled: boolean;
  rules: PortalRule[];
  yesnorules: PortalYesNoRule[];

  finalnumbersrequired: boolean;
  finalnumbersduebeforebooking: number;
  mustpayoutstandingbalance: boolean;
  limitchangesperbooking: boolean;
  changesallowed: number;
  changenumberofguestsallowed: boolean;
  allowbookingmove: boolean;
  limitbookingmove: boolean;
  bookingmovelimit: number;
  voucherdiscountid?: string | null;
  cannoteditonday: boolean;
  editcutoffhours: number;
}

const DEFAULT_RULES: PortalRule[] = [
  { key: 'waivers',          access: 'view' },
  { key: 'status',           access: 'view' },
  { key: 'payment',          access: 'view' },
  { key: 'extras',           access: 'view' },
  { key: 'bookingquestions', access: 'view' },
];

const DEFAULT_YESNO_RULES: PortalYesNoRule[] = [
  { key: 'changeDateAndTime',        enabled: false },
  { key: 'venueInformation',         enabled: true  },
  { key: 'emailVenue',               enabled: false },
  { key: 'callVenue',                enabled: false },
  { key: 'allowPartialPayments',     enabled: false },
];


@Component({
  selector: 'app-customerportal',
  templateUrl: './customerportal.component.html',
  styleUrls: ['./customerportal.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CustomerportalComponent implements OnInit {

  VenueID: string = localStorage.getItem('SmartVenueID')!;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  httpResponse: any;

  stats: PortalStatItem[] = [];
  statsLoading: boolean = false;

  templates: CustomerPortalTemplate[] = [];
  selectedTemplate: CustomerPortalTemplate | null = null;

  packages: any[] = [];
  templateDropdownOptions: { label: string; value: string | null }[] = [];
  discountOptions: { label: string; value: string }[] = [];

  editingTemplate: boolean = false;

  accessOptions = [
    { label: 'Hidden', value: 'hidden' },
    { label: 'View',   value: 'view'   },
    { label: 'Edit',   value: 'edit'   },
  ];

  yesNoOptions = [
    { label: 'Yes', value: true  },
    { label: 'No',  value: false },
  ];

  yesNoOptionsInverted = [
    { label: 'Yes', value: false },
    { label: 'No',  value: true  },
  ];


  statusOptions = [
    { label: 'Yes', value: 'view'   },
    { label: 'No',  value: 'hidden' },
  ];

  readonly ruleLabels: Record<string, string> = {
    waivers:          'Waivers',
    status:           'Show Status',
    payment:          'Payment',
    extras:           'Extras',
    bookingquestions: 'Booking Questions',
  };

  readonly yesNoLabels: Record<string, string> = {
    changeDateAndTime:     'Change Date & Time',
    emailVenue:            'Email Venue',
    callVenue:             'Call Venue',
    venueInformation:      'Show Venue Information',
    allowPartialPayments:  'Allow Partial Payments on Customer Portal',
  };

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private busyService: BusyService
  ) {}

  ngOnInit(): void {
    let userid = localStorage.getItem('SmartUserID');

    this.http.get(bmxurl + 'User/' + userid).subscribe({
      next: (response: any) => {
        this.httpResponse = response;
        if (this.httpResponse.succeeded) {
          let user = this.httpResponse.data;
          if (getAccess('CLASSES', user)) {
            this.AccessAllowed = true;
          } else {
            this.AccessAllowed = false;
          }
        }
        this.CompletedLoading = true;
        this.loadTemplates();
        this.loadDiscounts();
        this.loadStats();
      },
      error: () => {
        this.AccessAllowed = true;
        this.CompletedLoading = true;
        this.loadTemplates();
        this.loadDiscounts();
        this.loadStats();
      }
    });
  }

  loadStats(): void {
    this.statsLoading = true;
    this.http.get(bmxurl + 'portalstatreport/' + this.VenueID).subscribe({
      next: (response: any) => {
        this.statsLoading = false;
        if (response.succeeded) {
          this.stats = response.data as PortalStatItem[];
        }
      },
      error: () => { this.statsLoading = false; }
    });
  }

  statValue(action: string): number {
    return this.stats.find(s => s.action === action)?.value ?? 0;
  }

  private normaliseTemplate(t: CustomerPortalTemplate): CustomerPortalTemplate {
    t.finalnumbersrequired        = t.finalnumbersrequired        ?? false;
    t.finalnumbersduebeforebooking = t.finalnumbersduebeforebooking ?? 14;
    t.mustpayoutstandingbalance   = t.mustpayoutstandingbalance   ?? false;
    t.limitchangesperbooking      = t.limitchangesperbooking      ?? false;
    t.changesallowed              = t.changesallowed              ?? 3;
    t.changenumberofguestsallowed = t.changenumberofguestsallowed ?? false;
    t.allowbookingmove            = t.allowbookingmove            ?? false;
    t.limitbookingmove            = t.limitbookingmove            ?? false;
    t.bookingmovelimit            = t.bookingmovelimit            ?? 31;
    t.voucherdiscountid           = t.voucherdiscountid           ?? null;
    t.cannoteditonday             = t.cannoteditonday             ?? true;
    t.editcutoffhours             = t.editcutoffhours             ?? 0;

    // Strip any rules no longer in DEFAULT_RULES (e.g. upgrades)
    const validRuleKeys = new Set(DEFAULT_RULES.map(r => r.key));
    t.rules = (t.rules ?? []).filter(r => validRuleKeys.has(r.key));
    // Ensure all default rules exist
    for (const def of DEFAULT_RULES) {
      if (!t.rules.find(r => r.key === def.key)) {
        t.rules.push({ ...def });
      }
    }
    t.rules.sort((a, b) =>
      DEFAULT_RULES.findIndex(d => d.key === a.key) - DEFAULT_RULES.findIndex(d => d.key === b.key)
    );

    // Strip any yesno rules no longer in DEFAULT_YESNO_RULES
    const validYesNoKeys = new Set(DEFAULT_YESNO_RULES.map(r => r.key));
    t.yesnorules = (t.yesnorules ?? []).filter(r => validYesNoKeys.has(r.key));
    // Ensure all default yesno rules exist (handles templates saved before new rules were added)
    for (const def of DEFAULT_YESNO_RULES) {
      if (!t.yesnorules.find(r => r.key === def.key)) {
        t.yesnorules.push({ ...def });
      }
    }
    // Preserve original order
    t.yesnorules.sort((a, b) =>
      DEFAULT_YESNO_RULES.findIndex(d => d.key === a.key) - DEFAULT_YESNO_RULES.findIndex(d => d.key === b.key)
    );

    return t;
  }

  loadTemplates(): void {
    this.http.get(bmxurl + 'portaltemplatesList/' + this.VenueID + "/false").subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.templates = (response.data ?? []).map((t: CustomerPortalTemplate) => this.normaliseTemplate(t));
          this.selectedTemplate = this.templates.length > 0 ? this.templates[0] : null;
        } else {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: response.messages?.[0] ?? 'Could not load templates' });
        }
        this.templateDropdownOptions = [
          { label: 'No template', value: null },
          ...this.templates.map(t => ({ label: t.name, value: t.portaltemplateid })),
        ];
        this.loadPackages();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load portal templates' });
        this.templateDropdownOptions = [{ label: 'No template', value: null }];
        this.loadPackages();
      }
    });
  }

  saveTemplate(): void {
    if (!this.selectedTemplate) return;

    if (!this.selectedTemplate.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Please enter a template name before saving' });
      return;
    }

    const isNew = !this.selectedTemplate.portaltemplateid;
    const request = isNew
      ? this.http.post(bmxurl + 'portaltemplates', this.selectedTemplate)
      : this.http.put(bmxurl + 'portaltemplates', this.selectedTemplate);

    request.subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          if (isNew) {
            const saved: CustomerPortalTemplate = this.normaliseTemplate(response.data ?? this.selectedTemplate!);
            this.templates.push(saved);
          } else {
            const idx = this.templates.findIndex(t => t.portaltemplateid === this.selectedTemplate!.portaltemplateid);
            if (idx !== -1) this.templates[idx] = { ...this.selectedTemplate! };
          }
          this.templateDropdownOptions = [
            { label: 'No template', value: null },
            ...this.templates.map(t => ({ label: t.name, value: t.portaltemplateid })),
          ];
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Template saved successfully' });
          this.backToTemplateList();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: response.messages?.[0] ?? 'Save failed' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: 'Error saving template' });
      }
    });
  }

  selectTemplate(template: CustomerPortalTemplate): void {
    this.selectedTemplate = JSON.parse(JSON.stringify(template));
    this.editingTemplate = true;
  }

  backToTemplateList(): void {
    this.selectedTemplate = null;
    this.editingTemplate = false;
  }

  loadDiscounts(): void {
    this.http.get(bmxurl + 'DiscountList/' + this.VenueID + '/false').subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.discountOptions = (response.data ?? []).map((d: any) => ({
            label: d.name,
            value: d.discountid,
          }));
        }
      },
      error: () => {}
    });
  }

  loadPackages(): void {
    this.http.get(bmxurl + 'packagelist/' + this.VenueID + '/false').subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.packages = (response.data ?? []).map((pkg: any) => {
            const tid = pkg.portaltemplateid ?? null;
            const template = this.templates.find(t => t.portaltemplateid === tid);
            return {
              ...pkg,
              _portaltemplateid: template ? tid : null,
              _templateName: template?.name ?? '',
            };
          }).sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
        } else {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: response.messages?.[0] ?? 'Could not load packages' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load packages' });
      }
    });
  }

  getTemplateName(portaltemplateid: string | null): string {
    if (!portaltemplateid) return '';
    return this.templates.find(t => t.portaltemplateid === portaltemplateid)?.name ?? '';
  }

  getTemplateColour(portaltemplateid: string | null): string {
    if (!portaltemplateid) return 'transparent';
    return this.templates.find(t => t.portaltemplateid === portaltemplateid)?.colour ?? 'transparent';
  }

  onPackageTemplateChange(pkg: any): void {
    const updatedPkg = { ...pkg, portaltemplateid: pkg._portaltemplateid ?? null };
    this.busyService.busy();
    this.http.put(bmxurl + 'package', updatedPkg).subscribe({
      next: (response: any) => {
        this.busyService.idle();
        if (response.succeeded) {
          pkg.portaltemplateid = pkg._portaltemplateid;
          pkg._templateName = this.templates.find(t => t.portaltemplateid === pkg._portaltemplateid)?.name ?? '';
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: `Portal template updated for ${pkg.name}` });
        } else {
          pkg._portaltemplateid = pkg.portaltemplateid ?? null;
          this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: response.messages?.[0] ?? 'Save failed' });
        }
      },
      error: () => {
        this.busyService.idle();
        pkg._portaltemplateid = pkg.portaltemplateid ?? null;
        this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: 'Error saving portal template' });
      }
    });
  }

  get venueInfoEnabled(): boolean {
    return this.selectedTemplate?.yesnorules.find(r => r.key === 'venueInformation')?.enabled ?? true;
  }

onFinalNumbersRequiredChange(): void {
    if (!this.selectedTemplate!.finalnumbersrequired) {
      this.selectedTemplate!.finalnumbersduebeforebooking = 0;
    }
  }

  onCanEditOnDayChange(): void {
    if (this.selectedTemplate!.cannoteditonday) {
      this.selectedTemplate!.editcutoffhours = 0;
    }
  }

  onYesNoChange(rule: PortalYesNoRule): void {
    if (rule.key === 'venueInformation' && !rule.enabled) {
      this.selectedTemplate!.yesnorules.forEach(r => {
        if (r.key === 'emailVenue' || r.key === 'callVenue') {
          r.enabled = false;
        }
      });
    }
  }

  openNewTemplateDialog(): void {
    this.selectedTemplate = {
      id: null,
      portaltemplateid: null,
      venueid: this.VenueID,
      name: '',
      colour: '#4CAF50',
      disabled: false,
      rules: DEFAULT_RULES.map(r => ({ ...r })),
      yesnorules: DEFAULT_YESNO_RULES.map(r => ({ ...r })),
      finalnumbersrequired: false,
      finalnumbersduebeforebooking: 14,
      mustpayoutstandingbalance: false,
      limitchangesperbooking: false,
      changesallowed: 3,
      changenumberofguestsallowed: false,
      allowbookingmove: false,
      limitbookingmove: false,
      bookingmovelimit: 31,
      voucherdiscountid: null,
      cannoteditonday: true,
      editcutoffhours: 0,
    };
    this.editingTemplate = true;
  }

  deleteTemplate(template: CustomerPortalTemplate): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the template "${template.name}"?`,
      header: 'Delete Template',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.http.delete(bmxurl + 'portaltemplates/' + this.VenueID + '/' + template.portaltemplateid).subscribe({
          next: (response: any) => {
            if (response.succeeded) {
              this.templates = this.templates.filter(t => t.portaltemplateid !== template.portaltemplateid);
              if (this.selectedTemplate?.portaltemplateid === template.portaltemplateid) {
                this.selectedTemplate = null;
                this.editingTemplate = false;
              }
              this.templateDropdownOptions = [
                { label: 'No template', value: null },
                ...this.templates.map(t => ({ label: t.name, value: t.portaltemplateid })),
              ];
              this.packages.forEach(pkg => {
                if (pkg._portaltemplateid === template.portaltemplateid) {
                  pkg._portaltemplateid = null;
                  pkg._templateName = '';
                }
              });
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Template deleted' });
            } else {
              this.messageService.add({ severity: 'error', summary: 'Not Deleted', detail: response.messages?.[0] ?? 'Delete failed' });
            }
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Not Deleted', detail: 'Error deleting template' });
          }
        });
      }
    });
  }
}
